import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSelector,
    createSlice,
} from '@reduxjs/toolkit';
import { urlParamReplicaId, urlImportPrecheck, urlImportUpload } from '../../Constants';
import { IAction, IAppReduxStore } from '../store';
import { IImportStructureFields } from './sliceImportViewData';
import { getFileTypeOrDefault } from '../../util/FileUtils';
import { IRelatedDocument } from '../../components/General/Dialogs/_parts/DocumentRow';
import { IProjectFileType } from '../project/sliceProjectFileTypes';
import { FileInfo } from '../general/files';

export interface IProjectFileUploadState {
    fileModels: Array<{ id: string } & FileStateModel>;
    failedUploads: Array<{ fileName: string; timestamp: number }>;
}

export interface FileBasicInfo {
    fileName: string;
    fileSize: number;
    magicBytes: string;
}

interface FilePrecheckRequest extends FileBasicInfo {
    docType: string;
}

export interface FilePrecheckResponse {
    fileName: string;
    isValid: boolean;
    reason: string | null;
}

export interface FileUploadResponse {
    isUploaded: boolean;
    reason: string | null;
}

export interface FilePrecheckState {
    isLoading: boolean;
    isPrechecked: boolean;
    isValid: boolean;
    reason: string;
}

export interface FileUploadState {
    isLoading: boolean;
    isUploaded: boolean;
    uploadProgress: number;
    reason: string;
    responseTimestamp?: number;
}

export interface FileStateModel extends FileBasicInfo {
    precheckState: FilePrecheckState;
    uploadState: FileUploadState;
}

const initialState: IProjectFileUploadState = {
    fileModels: [],
    failedUploads: [],
};

const reducers = {
    resetFileState(state: IProjectFileUploadState) {
        Object.assign(state, initialState);
    },

    appendFiles(
        state: IProjectFileUploadState,
        action: IAction<Array<{ id: string } & FileBasicInfo>>,
    ) {
        const newFileModels = state.fileModels.slice();

        action.payload.forEach(fileInfo => {
            if (!newFileModels.some(existing => existing.id === fileInfo.id)) {
                newFileModels.push({
                    ...fileInfo,
                    precheckState: {
                        isLoading: false,
                        isPrechecked: false,
                        isValid: false,
                        reason: '',
                    },
                    uploadState: {
                        isLoading: false,
                        isUploaded: false,
                        uploadProgress: 0,
                        reason: '',
                    },
                });
            }
        });

        state.fileModels = newFileModels;
    },

    updateFile(state: IProjectFileUploadState, action: IAction<{ id: string } & FileStateModel>) {
        const newModel = action.payload;

        const newArray = state.fileModels.slice();

        const i = newArray.findIndex(item => item.id === newModel.id);
        if (i > -1) {
            newArray[i] = newModel;
        }

        state.fileModels = newArray;
    },

    updateProgress(
        state: IProjectFileUploadState,
        action: IAction<{ id: string; uploadedBytes: number }>,
    ) {
        const { id, uploadedBytes } = action.payload;

        const newArray = state.fileModels.slice();

        const i = newArray.findIndex(item => item.id === id);
        if (i > -1) {
            newArray[i].uploadState.uploadProgress = uploadedBytes;
        }

        state.fileModels = newArray;
    },

    removeFile(state: IProjectFileUploadState, action: IAction<string>) {
        const id = action.payload;

        state.fileModels = state.fileModels.filter(item => item.id !== id);
    },
};

const thunks = {
    precheckFile: async (params: {
        replicaId: string;
        fileInfo: { id: string } & FileBasicInfo;
        docType: string;
    }): Promise<FilePrecheckResponse> => {
        return new Promise((resolve, reject) => {
            try {
                const url = urlImportPrecheck.replace(urlParamReplicaId, params.replicaId);

                const request = new XMLHttpRequest();
                request.open('POST', url);
                request.setRequestHeader('Content-Type', 'application/json');
                request.onerror = () => {
                    reject(
                        new Error(
                            `File precheck failed ([${request.status}] ${request.statusText})`,
                        ),
                    );
                };
                request.onload = () => {
                    if (request.status !== 200) {
                        reject(
                            new Error(
                                `File precheck failed ([${request.status}] ${request.statusText})`,
                            ),
                        );
                    }
                    resolve(JSON.parse(request.response) as FilePrecheckResponse);
                };

                const payload: FilePrecheckRequest = {
                    ...params.fileInfo,
                    docType: params.docType,
                };
                request.send(JSON.stringify(payload));
            } catch {
                return reject(new Error('File precheck failed.'));
            }
        });
    },

    uploadFile: async (
        params: {
            replicaId: string;
            modelId: string;
            file: File;
            docType: string;
            categories: IImportStructureFields[];
            packages: IImportStructureFields[];
        },
        thunkApi,
    ): Promise<FileUploadResponse> => {
        return new Promise((resolve, reject) => {
            try {
                const url = urlImportUpload.replace(urlParamReplicaId, params.replicaId);

                const request = new XMLHttpRequest();
                request.open('POST', url);
                request.onerror = () => {
                    reject(
                        new Error(`File upload failed ([${request.status}] ${request.statusText})`),
                    );
                };
                request.upload.onprogress = e => {
                    thunkApi.dispatch(
                        updateProgress({ id: params.modelId, uploadedBytes: e.loaded }),
                    );
                };
                request.onload = () => {
                    if (request.status !== 200) {
                        reject(
                            new Error(
                                `File upload failed ([${request.status}] ${request.statusText})`,
                            ),
                        );
                    }
                    resolve(JSON.parse(request.response) as FileUploadResponse);
                };

                const form = new FormData();
                form.append('file', params.file);
                form.append('docType', params.docType);
                form.append('categories', JSON.stringify(params.categories));
                form.append('packages', JSON.stringify(params.packages));
                request.send(form);
            } catch {
                return reject(new Error('File upload failed.'));
            }
        });
    },

    _reducers: (builder: ActionReducerMapBuilder<IProjectFileUploadState>) => {
        builder.addCase(precheckFile.pending, (state, action) => {
            const model = state.fileModels.find(item => item.id === action.meta.arg.fileInfo.id);

            if (model) {
                reducers.updateFile(state, {
                    type: '',
                    payload: {
                        ...model,
                        precheckState: {
                            ...model.precheckState,
                            isLoading: true,
                        },
                    },
                });
            }
        });
        builder.addCase(precheckFile.fulfilled, (state, action) => {
            const precheckResult = action.payload;
            const model = state.fileModels.find(item => item.id === action.meta.arg.fileInfo.id);

            if (model) {
                reducers.updateFile(state, {
                    type: '',
                    payload: {
                        ...model,
                        precheckState: {
                            isLoading: false,
                            isPrechecked: true,
                            isValid: precheckResult.isValid,
                            reason: precheckResult.reason,
                        },
                    },
                });
            }
        });
        builder.addCase(precheckFile.rejected, (state, action) => {
            const model = state.fileModels.find(item => item.id === action.meta.arg.fileInfo.id);

            if (model) {
                reducers.updateFile(state, {
                    type: '',
                    payload: {
                        ...model,
                        precheckState: {
                            ...model.precheckState,
                            isLoading: false,
                            isPrechecked: true,
                            isValid: false,
                            reason: 'PRECHECK_FAILED',
                        },
                    },
                });
            }
        });

        builder.addCase(uploadFile.pending, (state, action) => {
            const model = state.fileModels.find(item => item.id === action.meta.arg.modelId);

            if (model) {
                reducers.updateFile(state, {
                    type: '',
                    payload: {
                        ...model,
                        uploadState: {
                            ...model.uploadState,
                            isLoading: true,
                        },
                    },
                });
            }
        });

        builder.addCase(uploadFile.fulfilled, (state, action) => {
            const uploadResult = action.payload;
            const model = state.fileModels.find(item => item.id === action.meta.arg.modelId);

            if (model) {
                reducers.updateFile(state, {
                    type: '',
                    payload: {
                        ...model,
                        uploadState: {
                            ...model.uploadState,
                            isLoading: false,
                            isUploaded: uploadResult.isUploaded,
                            reason: uploadResult.reason,
                            responseTimestamp: Date.now(),
                        },
                    },
                });
            }
        });

        builder.addCase(uploadFile.rejected, (state, action) => {
            const model = state.fileModels.find(item => item.id === action.meta.arg.modelId);

            if (model) {
                reducers.updateFile(state, {
                    type: '',
                    payload: {
                        ...model,
                        uploadState: {
                            ...model.uploadState,
                            isLoading: false,
                            isUploaded: false,
                            uploadProgress: 0,
                            reason: 'UPLOAD_REJECTED',
                            responseTimestamp: Date.now(),
                        },
                    },
                });

                state.failedUploads = state.failedUploads.concat({
                    fileName: model.fileName,
                    timestamp: model.uploadState.responseTimestamp ?? 0,
                });
            }
        });
    },
};

export const precheckFile = createAsyncThunk('project/file/precheck', thunks.precheckFile);
export const uploadFile = createAsyncThunk('project/file/upload', thunks.uploadFile);

export const projectFileUploadSlice = createSlice({
    name: 'projectFileUploadSlice',
    initialState,
    reducers,
    extraReducers: thunks._reducers,
});

export const { appendFiles, updateFile, updateProgress, removeFile, resetFileState } =
    projectFileUploadSlice.actions;

export default projectFileUploadSlice.reducer;

const filesSelector = (state: IAppReduxStore) => state.projectFileUpload.fileModels;

export const uploadedFilesSelector = createSelector([filesSelector], files =>
    files
        .filter(x => x.uploadState.isUploaded)
        .sort((a, b) => b.uploadState.responseTimestamp - a.uploadState.responseTimestamp),
);
export const validFilesSelector = createSelector([filesSelector], files =>
    files.filter(x => x.precheckState.isPrechecked && x.precheckState.isValid),
);
export const notPrecheckedFilesSelector = createSelector([filesSelector], files =>
    files.filter(x => !x.precheckState.isPrechecked && !x.precheckState.isLoading),
);
export const invalidFilesSelector = createSelector([filesSelector], files =>
    files.filter(x => x.precheckState.isPrechecked && !x.precheckState.isValid),
);
export const precheckActiveSelector = createSelector([filesSelector], files =>
    files.some(x => x.precheckState.isLoading),
);
export const uploadActiveSelector = createSelector([filesSelector], files =>
    files.some(x => x.uploadState.isLoading),
);
export const uploadFinishedSelector = createSelector([filesSelector], files => {
    if (files.length === 0) return false;

    const successfulUploads = files.filter(x => x.uploadState.isUploaded);
    const failedUploads = files.filter(x => x.uploadState.reason);

    if (successfulUploads.length + failedUploads.length !== files.length) {
        return false;
    }

    return {
        succeed: successfulUploads.length,
        failed: failedUploads.length,
        total: files.length,
    };
});

export const mapFileToRelatedDocument = (
    fileModel:
        | ({
              id: string;
          } & FileStateModel)
        | FileInfo,
    fileType: IProjectFileType,
): IRelatedDocument => {
    return {
        universalId: fileModel.id,
        title: fileModel.fileName,
        size: fileModel.fileSize,
        iconProps: {
            path: fileType.icon,
            color: fileType.color,
        },
    };
};

export const mapFilesToRelatedDocuments = (
    fileModels: ({
        id: string;
    } & FileStateModel)[],
) => fileModels.map(x => mapFileToRelatedDocument(x, getFileTypeOrDefault(x.fileName)));

export const getMappedFilesToRelatedDocuments = (state: IAppReduxStore) =>
    mapFilesToRelatedDocuments(state.projectFileUpload.fileModels);
